import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_GLOSSARY_MAX,
  BRAND_STEPS_MAX,
  BRAND_THEME_CONTRAST_PAIRS,
  BrandPalette,
  BrandReadinessItem,
  BrandSpace,
  BrandTheme,
  BrandThemeMode,
  IssuerKey,
  LocalizedText,
  UpdateBrandSpaceRequest,
  WCAG_NON_TEXT,
  brandLogoTint,
  contrastRatio,
  isHexColor,
  meetsContrastAA,
  relativeLuminance,
  resolveBrandPalette,
} from '@mygate/common';
import { issuersService } from '@/services/issuers';

const { Title, Text, Paragraph } = Typography;

const READINESS_LABELS: Record<BrandReadinessItem['key'], string> = {
  logo: 'Logo',
  tagline: 'Tagline (de)',
  contrast: 'Readable theme (WCAG AA)',
  accentContrast: 'Accent and button labels at 4.5:1',
  primaryContrast: 'Buttons stand out from the page',
  assets: 'Has something to sell',
};

const THEME_FIELDS: { key: keyof Omit<BrandTheme, 'mode'>; label: string; hint: string }[] = [
  { key: 'background', label: 'Background', hint: 'Page background of the brand space' },
  { key: 'surface', label: 'Surface', hint: 'Cards and inputs' },
  { key: 'primary', label: 'Primary', hint: 'Hero, buttons' },
  { key: 'onPrimary', label: 'On primary', hint: 'Text on primary' },
  { key: 'text', label: 'Text', hint: 'Body text on background and cards' },
  { key: 'mutedText', label: 'Muted text', hint: 'Secondary text on background and cards' },
  {
    key: 'accent',
    label: 'Accent',
    hint: 'Links, labels and badges on background and cards; outlines buttons whose primary blends in',
  },
];

const THEME_COLOR_KEYS = THEME_FIELDS.map(field => field.key);

/** The palette the app would paint with — only once every colour of the theme being edited is valid. */
function previewPalette(theme: Partial<BrandTheme>): BrandPalette | null {
  const complete =
    (theme.mode === 'light' || theme.mode === 'dark') && THEME_COLOR_KEYS.every(k => isHexColor(theme[k]));
  return complete ? resolveBrandPalette(theme as BrandTheme) : null;
}

type FormValues = Omit<UpdateBrandSpaceRequest, 'isEnabled'>;

const localized = (text?: LocalizedText): LocalizedText => ({ de: text?.de ?? '', en: text?.en ?? '' });

const toFormValues = (brand: BrandSpace): FormValues => ({
  displayName: brand.displayName,
  tagline: localized(brand.tagline),
  about: localized(brand.about),
  logoUrl: brand.logoUrl ?? '',
  markUrl: brand.markUrl ?? '',
  // '' is "follow the theme mode": saved as such, so switching the theme later carries the logo along.
  logoMode: brand.logoMode ?? '',
  heroImageUrl: brand.heroImageUrl ?? '',
  hero: {
    kicker: localized(brand.hero?.kicker),
    headline: localized(brand.hero?.headline),
    body: localized(brand.hero?.body),
  },
  howItWorksTitle: localized(brand.howItWorksTitle),
  howItWorks: (brand.howItWorks ?? []).map(step => ({ title: localized(step.title), body: localized(step.body) })),
  glossary: (brand.glossary ?? []).map(entry => ({
    term: localized(entry.term),
    definition: localized(entry.definition),
  })),
  welcome: {
    title: localized(brand.welcome?.title),
    body: localized(brand.welcome?.body),
    cta: localized(brand.welcome?.cta),
  },
  contacts: { ...brand.contacts },
  legal: { ...brand.legal },
  theme: { ...brand.theme },
  placement: brand.placement,
});

/** Empty optional strings become "unset" — the API treats '' as "clear", which is what a blanked field means. */
const stripEmptyEn = (text?: LocalizedText): LocalizedText | undefined =>
  text ? { de: text.de, ...(text.en?.trim() ? { en: text.en } : {}) } : undefined;

/** A localized field the form may leave untouched: `{ de: '' }` when nothing was typed in either language. */
const localizedOrBlank = (text?: LocalizedText): LocalizedText => stripEmptyEn(text) ?? { de: '' };

/** Narrows antd's validateFields rejection (ValidateErrorEntity) without reaching for `any`. */
type ValidationFailure = { errorFields: { name: (string | number)[] }[] };

const isValidationFailure = (err: unknown): err is ValidationFailure =>
  typeof err === 'object' && err !== null && Array.isArray((err as ValidationFailure).errorFields);

const ratioLabel = (ratio: number): string => (Math.floor(ratio * 100) / 100).toFixed(2);

/** The pairs the backend requires at AA, then the advisory one: whether a primary fill shows as a shape. */
function ContrastBadge({ theme }: { theme: Partial<BrandTheme> }) {
  const primaryValid = isHexColor(theme.primary) && isHexColor(theme.background) && isHexColor(theme.surface);
  const primaryRatio = primaryValid
    ? Math.min(
        contrastRatio(theme.primary as string, theme.background as string),
        contrastRatio(theme.primary as string, theme.surface as string),
      )
    : 0;
  const primaryOk = primaryValid && primaryRatio >= WCAG_NON_TEXT;
  return (
    <Space wrap>
      {BRAND_THEME_CONTRAST_PAIRS.map(pair => {
        const fg = theme[pair.fg];
        const bg = theme[pair.bg];
        const valid = isHexColor(fg) && isHexColor(bg);
        const ratio = valid ? contrastRatio(fg as string, bg as string) : 0;
        // The accent and the button labels are set large and heavy in the app, so the backend holds them to
        // 3:1 and only advises above it; body text needs 4.5:1 outright (MYG-842).
        const largeText = pair.fg === 'accent' || pair.fg === 'onPrimary';
        const need = largeText ? WCAG_NON_TEXT : 4.5;
        const ok = valid && ratio >= need;
        const tight = ok && largeText && !meetsContrastAA(fg as string, bg as string);
        return (
          <Tag
            key={pair.label}
            color={!ok ? 'red' : tight ? 'orange' : 'green'}
            icon={!ok ? <CloseCircleOutlined /> : tight ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
          >
            {pair.label}: {valid ? `${ratioLabel(ratio)}:1` : '—'} (≥ {need}
            {largeText ? ', 4.5 advised' : ''})
          </Tag>
        );
      })}
      <Tag
        color={primaryOk ? 'green' : 'orange'}
        icon={primaryOk ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
      >
        Primary / background &amp; surface: {primaryValid ? `${ratioLabel(primaryRatio)}:1` : '—'} (≥ {WCAG_NON_TEXT},
        advisory — the app outlines buttons below it)
      </Tag>
    </Space>
  );
}

/**
 * The CSS stand-in for the app's logo tint: the app recolours the logo in the screen's text colour, which
 * a browser can only approximate as black or white — enough to show whether the logo would vanish.
 */
const logoFilter = (tint: string | undefined): string | undefined =>
  tint === undefined ? undefined : relativeLuminance(tint) > 0.5 ? 'brightness(0) invert(1)' : 'brightness(0)';

/** A list of localized entries — the steps and the glossary terms of the brand home. */
function LocalizedList({
  name,
  title,
  hint,
  max,
  fields,
  addLabel,
}: {
  name: 'howItWorks' | 'glossary';
  title: string;
  hint: string;
  max: number;
  fields: { key: string; label: string; rows?: number }[];
  addLabel: string;
}) {
  return (
    <>
      <Title level={5}>{title}</Title>
      <Paragraph type="secondary">{hint}</Paragraph>
      <Form.List name={name}>
        {(items, { add, remove }) => (
          <>
            {items.map((item, index) => (
              <Card
                key={item.key}
                size="small"
                title={`${index + 1}`}
                style={{ marginBottom: 12 }}
                extra={
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(item.name)}
                    aria-label="Remove"
                  />
                }
              >
                <Row gutter={16}>
                  {fields.map(field =>
                    (['de', 'en'] as const).map(lang => (
                      <Col span={12} key={`${field.key}-${lang}`}>
                        <Form.Item
                          name={[item.name, field.key, lang]}
                          label={`${field.label} (${lang})`}
                          rules={
                            lang === 'de' ? [{ required: true, whitespace: true, message: 'German is required' }] : []
                          }
                        >
                          {field.rows && field.rows > 1 ? <Input.TextArea rows={field.rows} /> : <Input />}
                        </Form.Item>
                      </Col>
                    )),
                  )}
                </Row>
              </Card>
            ))}
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} disabled={items.length >= max} block>
              {addLabel}
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
}

/** A miniature of the brand screens, painted with the palette the app derives from the theme being edited. */
function BrandPreview({
  theme,
  displayName,
  tagline,
  heroKicker,
  heroHeadline,
  logoUrl,
  logoMode,
}: {
  theme: Partial<BrandTheme>;
  displayName?: string;
  tagline?: string;
  heroKicker?: string;
  heroHeadline?: string;
  logoUrl?: string;
  logoMode?: BrandThemeMode | '';
}) {
  const palette = previewPalette(theme);
  if (!palette) {
    return <Alert type="info" showIcon message="The preview appears once every theme colour is a valid #RRGGBB." />;
  }
  const outline = palette.primaryOutline ? `1px solid ${palette.primaryOutline}` : 'none';
  const tint = brandLogoTint({ logoMode: logoMode || undefined, theme: palette }, palette);
  const chipStyle = {
    background: palette.accentSoft,
    color: palette.onAccentSoft,
    borderRadius: 10,
    padding: '4px 10px',
    fontSize: 12,
  };
  return (
    <div style={{ background: palette.background, borderRadius: 16, padding: 16, border: '1px solid #e5e5e5' }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="logo"
            style={{ height: 28, maxWidth: 120, objectFit: 'contain', filter: logoFilter(tint) }}
          />
        ) : (
          <div style={{ width: 28, height: 28, borderRadius: 14, background: palette.primaryMark }} />
        )}
        <span style={{ color: palette.text, fontWeight: 700 }}>{displayName || 'Brand'}</span>
      </Space>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, fontWeight: 600, color: palette.accent }}>
          {(heroKicker || 'Strategie, Erfahrung, Präzision').toUpperCase()}
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.05, color: palette.text }}>
          {heroHeadline || 'Reale Werte. Digital investierbar.'}
        </div>
      </div>
      <Space size={8} style={{ marginBottom: 12 }}>
        <div
          style={{
            background: palette.primary,
            color: palette.onPrimary,
            border: outline,
            borderRadius: 12,
            padding: '12px 16px',
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              background: palette.primaryTint,
              margin: '0 auto 6px',
            }}
          />
          Hotline
        </div>
        <div
          style={{
            background: palette.inverseSurface,
            color: palette.onInverseSurface,
            borderRadius: 12,
            padding: '12px 16px',
            fontSize: 12,
          }}
        >
          <div style={{ color: palette.accentOnInverse, fontWeight: 700 }}>TOKENISIERUNG</div>
          Kurz erklärt
        </div>
      </Space>
      <div
        style={{
          background: palette.warningSurface,
          color: palette.onWarningSurface,
          border: `1px solid ${palette.warningBorder}`,
          borderRadius: 12,
          padding: 10,
          fontSize: 12,
          marginBottom: 12,
        }}
      >
        Risikohinweis: Der Erwerb dieses Tokens ist mit erheblichen Risiken verbunden.
      </div>
      <div style={{ background: palette.surface, borderRadius: 12, padding: 12, color: palette.text }}>
        <div style={{ fontWeight: 600 }}>Bostela Audi R8 Token</div>
        <div style={{ color: palette.mutedText, fontSize: 12 }}>{tagline || 'Tagline'}</div>
        <Space wrap size={6} style={{ marginTop: 8 }}>
          <span style={chipStyle}>ab 100,00 €</span>
          <span style={chipStyle}>Risikoklasse 6</span>
          <span
            style={{
              background: palette.successSurface,
              color: palette.onSuccessSurface,
              borderRadius: 999,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            ● Live
          </span>
        </Space>
        <div style={{ color: palette.accent, fontSize: 12, marginTop: 8 }}>Mehr erfahren →</div>
      </div>
      <div
        style={{
          background: palette.primary,
          color: palette.onPrimary,
          border: outline,
          borderRadius: 12,
          padding: 12,
          marginTop: 12,
          textAlign: 'center',
          fontWeight: 700,
          letterSpacing: 1,
        }}
      >
        JETZT INVESTIEREN
      </div>
    </div>
  );
}

/**
 * Brand space of one issuer: branding, contacts, theme with live preview and the enable switch
 * behind its checklist (MYG-732).
 */
export function BrandSpacePage() {
  const navigate = useNavigate();
  const params = useParams<{ issuer: string }>();
  const issuer = params.issuer as IssuerKey;
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const seededRef = useRef<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['issuers', issuer, 'brand-space'],
    queryFn: async () => (await issuersService.getBrandSpace(issuer)).data,
    enabled: !!issuer,
  });

  useEffect(() => {
    if (data?.brand && seededRef.current !== issuer) {
      seededRef.current = issuer;
      form.setFieldsValue(toFormValues(data.brand));
    }
  }, [data, form, issuer]);

  const theme = Form.useWatch('theme', form) as Partial<BrandTheme> | undefined;
  const displayName = Form.useWatch('displayName', form) as string | undefined;
  const tagline = Form.useWatch(['tagline', 'de'], form) as string | undefined;
  const heroKicker = Form.useWatch(['hero', 'kicker', 'de'], form) as string | undefined;
  const heroHeadline = Form.useWatch(['hero', 'headline', 'de'], form) as string | undefined;
  const logoUrl = Form.useWatch('logoUrl', form) as string | undefined;
  const logoMode = Form.useWatch('logoMode', form) as BrandThemeMode | '' | undefined;

  const saveMutation = useMutation({
    mutationFn: (payload: UpdateBrandSpaceRequest) => issuersService.updateBrandSpace(issuer, payload),
    onSuccess: () => {
      message.success('Brand space saved');
      queryClient.invalidateQueries({ queryKey: ['issuers'] });
    },
    onError: err => message.error(err instanceof Error ? err.message : 'Failed to save the brand space'),
  });

  const onSave = async (): Promise<void> => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch (err) {
      if (isValidationFailure(err) && err.errorFields.length > 0) {
        form.scrollToField(err.errorFields[0].name);
        message.error('Please fix the highlighted fields');
      }
      return;
    }
    saveMutation.mutate({
      displayName: values.displayName,
      tagline: stripEmptyEn(values.tagline),
      about: stripEmptyEn(values.about),
      logoUrl: values.logoUrl ?? '',
      markUrl: values.markUrl ?? '',
      logoMode: values.logoMode ?? '',
      heroImageUrl: values.heroImageUrl ?? '',
      // Each block is replaced whole: a list emptied here clears it, a hero left blank hides it.
      hero: {
        kicker: localizedOrBlank(values.hero?.kicker),
        headline: localizedOrBlank(values.hero?.headline),
        body: localizedOrBlank(values.hero?.body),
      },
      howItWorksTitle: localizedOrBlank(values.howItWorksTitle),
      howItWorks: (values.howItWorks ?? []).map(step => ({
        title: localizedOrBlank(step.title),
        body: localizedOrBlank(step.body),
      })),
      glossary: (values.glossary ?? []).map(entry => ({
        term: localizedOrBlank(entry.term),
        definition: localizedOrBlank(entry.definition),
      })),
      welcome: {
        title: localizedOrBlank(values.welcome?.title),
        body: localizedOrBlank(values.welcome?.body),
        cta: localizedOrBlank(values.welcome?.cta),
      },
      contacts: values.contacts,
      legal: values.legal,
      theme: values.theme,
      placement: values.placement ?? undefined,
    });
  };

  const readiness = data?.readiness;
  const canEnable = readiness?.canEnable ?? false;
  const enabled = data?.brand.isEnabled ?? false;
  const failing = useMemo(() => readiness?.items.filter(i => !i.ok && !i.advisory) ?? [], [readiness]);

  return (
    <>
      <div className="page-toolbar">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/issuers')} type="text" />
          <Title level={4} style={{ margin: 0 }}>
            Brand space · {data?.brand.displayName ?? issuer}
          </Title>
          {data && (enabled ? <Tag color="green">live in the app</Tag> : <Tag>not visible</Tag>)}
        </Space>
      </div>
      <>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error instanceof Error ? error.message : 'Failed to load'}
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={24}>
          <Col xs={24} xl={14}>
            <Card loading={isLoading} title="Branding" style={{ marginBottom: 24 }}>
              <Form form={form} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name="displayName" label="Display name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name="placement" label="Order in the cockpit carousel">
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['tagline', 'de']} label="Tagline (de)">
                      <Input maxLength={120} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['tagline', 'en']} label="Tagline (en)">
                      <Input maxLength={120} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['about', 'de']} label="About (de)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['about', 'en']} label="About (en)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name="logoUrl" label="Logo URL (https)">
                      <Input placeholder="https://…/logo.png" />
                    </Form.Item>
                    <Form.Item
                      name="markUrl"
                      label="Mark URL (https, optional)"
                      tooltip="The Bildmarke on its own, without the wordmark: it fills the circles a wordmark does not fit — the avatar on an asset card and the plate on the “Über …” tile. Left blank, those keep their old artwork."
                    >
                      <Input placeholder="https://…/mark.svg" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name="heroImageUrl" label="Hero image URL (https, optional)">
                      <Input placeholder="https://…/hero.jpg" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="logoMode"
                      label="Logo is drawn for"
                      tooltip="On the other kind of background the app recolours the logo in the text colour — a black logo turns white on the dark app screens."
                    >
                      <Select
                        options={[
                          { value: '', label: 'The theme’s own background (follows Mode)' },
                          { value: 'light', label: 'A light background (dark logo)' },
                          { value: 'dark', label: 'A dark background (light logo)' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Title level={5}>Brand home copy</Title>
                <Paragraph type="secondary">
                  The opening words of the brand home. The app shows the block only while something is written; the
                  kicker is set in capitals, the headline exactly as written.
                </Paragraph>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name={['hero', 'kicker', 'de']} label="Kicker (de)">
                      <Input maxLength={80} placeholder="Strategie, Erfahrung, Präzision" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['hero', 'kicker', 'en']} label="Kicker (en)">
                      <Input maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['hero', 'headline', 'de']} label="Headline (de)">
                      <Input.TextArea rows={2} maxLength={120} placeholder="Reale Werte. Digital investierbar." />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['hero', 'headline', 'en']} label="Headline (en)">
                      <Input.TextArea rows={2} maxLength={120} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['hero', 'body', 'de']} label="Intro (de)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['hero', 'body', 'en']} label="Intro (en)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                </Row>
                <Title level={5}>Heading over the steps</Title>
                <Paragraph type="secondary">
                  Optional. The app prints its own &quot;Wie funktioniert das?&quot; while this is blank — a brand that
                  titles its steps differently (&quot;Was hinter TRADEVEST steht&quot;) writes it here.
                </Paragraph>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name={['howItWorksTitle', 'de']} label="Heading (de)">
                      <Input maxLength={80} placeholder="Wie funktioniert das?" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['howItWorksTitle', 'en']} label="Heading (en)">
                      <Input maxLength={80} />
                    </Form.Item>
                  </Col>
                </Row>
                <LocalizedList
                  name="howItWorks"
                  title="How does it work? — steps"
                  hint={`Numbered 01, 02, 03 on the brand home; at most ${BRAND_STEPS_MAX}. Leave the list empty to hide the block.`}
                  max={BRAND_STEPS_MAX}
                  fields={[
                    { key: 'title', label: 'Title' },
                    { key: 'body', label: 'Text', rows: 3 },
                  ]}
                  addLabel="Add a step"
                />
                <LocalizedList
                  name="glossary"
                  title="In short — glossary"
                  hint={`Term and definition on the dark "Kurz erklärt" card; at most ${BRAND_GLOSSARY_MAX}. Leave the list empty to hide the card.`}
                  max={BRAND_GLOSSARY_MAX}
                  fields={[
                    { key: 'term', label: 'Term' },
                    { key: 'definition', label: 'Definition', rows: 3 },
                  ]}
                  addLabel="Add a term"
                />
                <Title level={5}>Welcome sheet</Title>
                <Paragraph type="secondary">
                  Opens over the brand home the first time a reader enters it, once per device. Shown only while a title
                  or a paragraph is written; the button reads &quot;Los gehts&quot; unless you write otherwise.
                </Paragraph>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'title', 'de']} label="Title (de)">
                      <Input maxLength={80} placeholder="Willkommen im … Space" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'title', 'en']} label="Title (en)">
                      <Input maxLength={80} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'body', 'de']} label="Text (de)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'body', 'en']} label="Text (en)">
                      <Input.TextArea rows={4} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'cta', 'de']} label="Button (de)">
                      <Input maxLength={40} placeholder="Los gehts" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['welcome', 'cta', 'en']} label="Button (en)">
                      <Input maxLength={40} />
                    </Form.Item>
                  </Col>
                </Row>
                <Title level={5}>Contacts</Title>
                <Paragraph type="secondary">
                  Each becomes a tile on the brand home: website (behind &quot;About&quot;), hotline, support e-mail,
                  and the contact person with their portrait.
                </Paragraph>
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['contacts', 'website']} label="Website">
                      <Input placeholder="https://…" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['contacts', 'email']} label="Support e-mail">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['contacts', 'phone']} label="Hotline">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['contacts', 'contactPerson']} label="Contact person">
                      <Input placeholder="Max Muster" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name={['contacts', 'contactPersonRole']} label="Contact person — role">
                      <Input placeholder="Investor Relations" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={['contacts', 'contactPersonPhotoUrl']}
                      label="Contact person — portrait URL (https)"
                    >
                      <Input placeholder="https://…/portrait.jpg" />
                    </Form.Item>
                  </Col>
                </Row>
                <Title level={5}>Legal</Title>
                <Paragraph type="secondary">
                  Stated in the invest flow before the purchase: who issues, under which law.
                </Paragraph>
                <Row gutter={16}>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['legal', 'entityName']} label="Issuing entity">
                      <Input placeholder="Lympid Digital Assets S.A." />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={12}>
                    <Form.Item name={['legal', 'governingLaw']} label="Governing law">
                      <Input placeholder="Portuguese law" />
                    </Form.Item>
                  </Col>
                </Row>
                <Title level={5}>Theme</Title>
                <Paragraph type="secondary">
                  The brand paints the whole space, background included. Every colour is #RRGGBB; the text pairs below
                  must meet WCAG AA before the brand can go live. Dividers, chips, the risk warning and status colours
                  are derived by the app and always readable — the preview shows them.
                </Paragraph>
                <Row gutter={16}>
                  <Col xs={24} sm={12} xl={8}>
                    <Form.Item name={['theme', 'mode']} label="Mode">
                      <Select
                        options={[
                          { value: 'light', label: 'Light' },
                          { value: 'dark', label: 'Dark' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  {THEME_FIELDS.map(field => (
                    <Col xs={24} sm={12} xl={8} key={field.key}>
                      <Form.Item
                        name={['theme', field.key]}
                        label={field.label}
                        tooltip={field.hint}
                        rules={[
                          { required: true, message: '#RRGGBB' },
                          {
                            validator: (_, value) =>
                              value === undefined || value === '' || isHexColor(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('#RRGGBB')),
                          },
                        ]}
                      >
                        <Input
                          addonBefore={
                            <span
                              style={{
                                display: 'inline-block',
                                width: 14,
                                height: 14,
                                borderRadius: 7,
                                background: isHexColor(theme?.[field.key]) ? theme?.[field.key] : '#ccc',
                                border: '1px solid #ddd',
                              }}
                            />
                          }
                        />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
                <ContrastBadge theme={theme ?? {}} />
                <div style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => void onSave()}
                    loading={saveMutation.isPending}
                  >
                    Save
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
          <Col xs={24} xl={10}>
            <Card title="Preview" style={{ marginBottom: 24 }}>
              <BrandPreview
                theme={theme ?? {}}
                displayName={displayName}
                tagline={tagline}
                heroKicker={heroKicker}
                heroHeadline={heroHeadline}
                logoUrl={logoUrl}
                logoMode={logoMode}
              />
            </Card>
            <Card title="Visibility" loading={isLoading}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space direction="vertical" size={4}>
                  {readiness?.items.map(item => (
                    <Space key={item.key}>
                      {item.ok ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : item.advisory ? (
                        <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      )}
                      <Text>
                        {READINESS_LABELS[item.key]}
                        {item.advisory ? ' (advisory)' : ''}
                      </Text>
                      {!item.ok && item.detail && <Text type="secondary">— {item.detail}</Text>}
                    </Space>
                  ))}
                  <Text type="secondary">Active assets of this issuer: {data?.activeAssetCount ?? 0}</Text>
                </Space>
                <Space>
                  <Switch
                    checked={enabled}
                    disabled={!enabled && !canEnable}
                    loading={saveMutation.isPending}
                    onChange={checked => saveMutation.mutate({ isEnabled: checked })}
                  />
                  <Text>{enabled ? 'Shown in the cockpit and open in the app' : 'Hidden'}</Text>
                </Space>
                {!enabled && !canEnable && failing.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Save the branding first — the switch unlocks once every item above passes."
                  />
                )}
                {!data?.configured && (
                  <Alert type="info" showIcon message="Nothing saved yet — the form shows the defaults." />
                )}
              </Space>
            </Card>
          </Col>
        </Row>
      </>
    </>
  );
}
